import { Music2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

/**
 * OKS-628 — Graceful fallback for the candidate's TikTok profile embed
 * (UX Spec §3 States 3/4/5 — one block for missing/invalid handle,
 * private/deleted account, and oEmbed error/timeout alike).
 *
 * Muted + informational (NOT an error): branded glyph + a one-line "not
 * available" message + the `@handle` as an outbound link (new tab,
 * `rel="noopener"`) when a handle string exists. The fallback state is
 * conveyed by real text, never colour/icon alone (a11y — UX Spec §6).
 *
 * OKS-1007 — `min-h-[220px]` is the SHARED floor, and it is load-bearing, not
 * cosmetic. `TiktokProfileSectionSkeleton` and `TiktokProfileEmbed` both
 * reserve exactly this; before OKS-1007 the fallback reserved nothing, so the
 * deferred skeleton→fallback swap CONTRACTED the section by a measured 44px
 * and pulled everything below it up — including the contact panel's radios,
 * which are 16px tall. A pointer already pressed on a radio then released 44px
 * away, `click` fired on the common ancestor, and the channel selection was
 * silently lost (OKS-1007; the same swap is in `candidate-review-card.tsx`).
 *
 * ⚠️ SCOPE OF THE GUARANTEE. The floor makes the DEFERRED SWAP shift-free —
 * skeleton, fallback and the embed's initial blockquote all occupy it. It does
 * NOT make the section shift-free in general: `<TiktokProfileEmbed>` has no
 * upper bound, so when TikTok's `embed.js` injects its `<iframe>` the container
 * grows past this floor and the column moves again (uncapped on purpose — a cap
 * would crop a real embed). OKS-1015 keeps a PRESS safe from that growth: the
 * embed freezes its own height while a press is held. Do not read this comment
 * as "no layout shift" — read it as "no shift from the deferred swap".
 *
 * Enforced by `__tests__/tiktok-profile-fallback.test.tsx` (footprint) and by
 * the Probe C bounding-box assertion in
 * `tests/playwright/contact-panel-deferred-race-oks1007.spec.ts`. Changing the
 * value in one place without the others is what let this drift the first time.
 *
 * Locked testids: `tiktok-profile-embed-fallback`, `tiktok-profile-embed-glyph`.
 */
interface TiktokProfileFallbackProps {
  /** Candidate TikTok handle (no `@`); may be empty in defensive callers. */
  handle: string
  className?: string
}

export function TiktokProfileFallback({
  handle,
  className,
}: TiktokProfileFallbackProps) {
  const { t } = useTranslation()
  const trimmed = handle.trim()

  return (
    <div
      data-testid="tiktok-profile-embed-fallback"
      className={cn(
        "bg-muted/40 text-muted-foreground flex min-h-[220px] flex-col items-center justify-center gap-2 rounded-xl px-4 py-8 text-center",
        className,
      )}
    >
      <Music2
        aria-hidden="true"
        className="size-8 opacity-70"
        data-testid="tiktok-profile-embed-glyph"
      />
      <p className="text-sm">
        {t("candidates.detail.tiktok_profile.unavailable")}
      </p>
      {trimmed && (
        <a
          href={`https://www.tiktok.com/@${trimmed}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-foreground inline-flex min-h-11 items-center font-mono text-sm underline-offset-4 hover:underline focus-visible:underline"
        >
          {t("candidates.detail.tiktok_profile.open_on_tiktok", {
            handle: trimmed,
          })}
        </a>
      )}
    </div>
  )
}
